"use client";

import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import { IconCheck, IconLineDashed as IconIndeterminate } from "@tabler/icons-react";
import clsx from "clsx";
import type React from "react";
import { useRef, useState } from "react";
import { composeRefs } from "../../../lib/composeRefs";
import { createEvent } from "../../../lib/EventUtils";
import type { ControlSize } from "../../../lib/FrontendTypes";
import Ripple from "../../common/Ripple/Ripple";
import type { InputProps } from "../InputTypes";
import "./Checkbox.css";

const styles: Record<string, string> = {
  root: "calmlens-checkbox-root",
  indicator: "calmlens-checkbox-indicator",
  indeterminate: "calmlens-checkbox-indeterminate",
  checked: "calmlens-checkbox-checked",
};

export interface CheckboxProps extends InputProps<boolean>, React.AriaAttributes {
  className?: string;
  ref?: React.Ref<HTMLElement>;
  size?: ControlSize;
  required?: boolean;
  readOnly?: boolean;
}

const Checkbox = (props: CheckboxProps) => {
  const {
    value,
    onValueProcess,
    ref,
    size,
    className,
    defaultValue,
    onChange,
    name,
    onValueChange,
    ...otherProps
  } = props;
  const selfRef = useRef<HTMLElement>(null);

  const [stateValue, setStateValue] = useState<boolean | undefined>(undefined);

  const currentValue = value ?? stateValue ?? defaultValue;

  return (
    <BaseCheckbox.Root
      ref={composeRefs(selfRef, ref)}
      className={clsx(styles.root, className)}
      checked={Boolean(currentValue)}
      indeterminate={currentValue === undefined || currentValue === null}
      name={name}
      data-size={size}
      onCheckedChange={(checked) => {
        const value = checked;
        onValueChange?.(value);
        setStateValue(value);
        onChange?.(createEvent("change", { value: value, name, checked: value }));
      }}
      {...otherProps}
    >
      {!props.disabled && <Ripple />}
      {/* @ts-ignore */}
      <BaseCheckbox.Indicator className={styles.indicator}>
        <IconCheck className={styles.checked} aria-hidden="true" />
        <IconIndeterminate className={styles.indeterminate} aria-hidden="true" />
      </BaseCheckbox.Indicator>
    </BaseCheckbox.Root>
  );
};

export default Checkbox;
